import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/ArrowUpwardRounded";
import StopIcon from "@material-ui/icons/StopRounded";
import BuildIcon from "@material-ui/icons/BuildOutlined";
import PhoneIcon from "@material-ui/icons/PhoneOutlined";
import ArrowFwdIcon from "@material-ui/icons/ArrowForwardRounded";

import { SEEDS, hexA } from "./heroSeeds";
import { getMessages, streamMessage, placeCall, getCallStatus } from "../../services/chatApi";
import api from "../../services/api";
import { useChat } from "../../context/Chat/ChatContext";
import { useProject } from "../../context/Project/ProjectContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import Markdown from "../../components/Markdown";
import AskWidget from "../../components/AskWidget";
import CallArtifact from "./CallArtifact";

// extrai os marcadores do assistente e devolve o texto limpo:
//   [[BUILD]] <spec>            → montar fluxo no Builder
//   [[CALL]] <número> | <tarefa> → ligação pontual com objetivo
// esconde também um marcador ainda incompleto no fim do stream.
const parseMarkers = text => {
  let t = text || "";
  let build = null;
  let call = null;
  const bi = t.indexOf("[[BUILD]]");
  if (bi >= 0) {
    build = t.slice(bi + 9).trim() || null;
    t = t.slice(0, bi).trimEnd();
  }
  const ci = t.indexOf("[[CALL]]");
  if (ci >= 0) {
    const raw = t.slice(ci + 8).trim();
    const pipe = raw.indexOf("|");
    const to = (pipe >= 0 ? raw.slice(0, pipe) : raw).replace(/\D/g, "");
    const task = pipe >= 0 ? raw.slice(pipe + 1).trim() : "";
    if (to) call = { to, task };
    t = t.slice(0, ci).trimEnd();
  }
  // streaming: corta um marcador ainda incompleto no fim ("[[B…" / "[[C…")
  const partial = t.match(/\[\[[BC][\s\S]*$/);
  if (partial) t = t.slice(0, partial.index).trimEnd();
  return { clean: t, build, call };
};

// status finais do jambonz (encerram o polling da ligação)
const TERMINAL = ["completed", "failed", "busy", "no-answer", "no_answer", "canceled", "cancelled", "rejected"];
const isInCall = st => ["in-progress", "in_progress", "answered"].includes(String(st || "").toLowerCase());

// saudação proativa: a IA "puxa" a conversa assim que abrimos um chat novo.
// Calibração: exibida em TODA conversa nova (depois mudamos para só a 1ª vez).
const buildGreeting = name => {
  const first = (name || "").trim().split(/\s+/)[0];
  return `Oi${first ? ` ${first}` : ""}! O que você quer montar? Descreva em uma frase — eu monto na sua frente, sem você configurar nada agora.`;
};

const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// #RRGGBB -> rgba com alfa (fundo suave do ícone da semente na cor da categoria)
// marca "sparkle" (estrela de 4 pontas) — herda cor e tamanho via currentColor/1em
const Sparkle = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.4l1.7 5.1a4 4 0 0 0 2.8 2.8l5.1 1.7-5.1 1.7a4 4 0 0 0-2.8 2.8L12 21.6l-1.7-5.1a4 4 0 0 0-2.8-2.8L2.4 12l5.1-1.7a4 4 0 0 0 2.8-2.8L12 2.4z" />
    <path d="M5 3.2l.6 1.8a1.4 1.4 0 0 0 1 1l1.8.6-1.8.6a1.4 1.4 0 0 0-1 1L5 10l-.6-1.8a1.4 1.4 0 0 0-1-1L1.6 6.6l1.8-.6a1.4 1.4 0 0 0 1-1L5 3.2z" opacity=".85" />
  </svg>
);

const useStyles = makeStyles(theme => ({
  root: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, background: "#fff", fontFamily: FONT, position: "relative" },

  // overlay: o "chat" vira um card que ENCOLHE e ancora no canto inferior direito
  // (onde o copiloto vive no canvas) — como se minimizasse pra lá.
  launchOverlay: {
    position: "absolute", inset: 0, zIndex: 30, pointerEvents: "none", overflow: "hidden",
    background: "rgba(255,255,255,0.35)", animation: "$launchScrim .3s ease both",
  },
  "@keyframes launchScrim": { from: { opacity: 0 }, to: { opacity: 1 } },
  launchCard: {
    position: "absolute", left: "50%", top: "46%", width: 360, maxWidth: "84%",
    background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 16,
    boxShadow: "0 18px 50px rgba(27,34,42,.22)", padding: 16, textAlign: "left",
    transformOrigin: "center",
    animation: "$dockBR 1s cubic-bezier(.4,0,.2,1) both",
  },
  // 1) EMERGE no centro (pop) e SEGURA mais tempo → 2) deriva pro canto inferior
  // direito e some ANTES de chegar (o copiloto é lançado antes do fim, então
  // brota do canto enquanto o card já sumiu — parece que virou a janela dele).
  "@keyframes dockBR": {
    "0%": { opacity: 0, transform: "translate(-50%,-42%) scale(.94)" },
    "12%": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" },
    "56%": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" },
    "70%": { opacity: 0, transform: "translate(11vw,8vh) scale(.7)" },
    "100%": { opacity: 0, transform: "translate(20vw,15vh) scale(.55)" },
  },
  launchHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12 },
  launchSparkle: {
    width: 30, height: 30, borderRadius: 9, flexShrink: 0,
    background: theme.app.dark, display: "flex", alignItems: "center", justifyContent: "center",
    "& svg": { fontSize: 16, color: "#CEFF00" },
  },
  launchTitle: { fontSize: 13.5, fontWeight: 600, color: theme.app.textSecondary, display: "inline-flex", alignItems: "center" },
  launchBubble: {
    background: "#F1F3F6", borderRadius: 12, padding: "10px 12px",
    fontSize: 14.5, fontWeight: 500, color: theme.app.ink, lineHeight: 1.4,
  },
  scroll: { flex: 1, overflowY: "auto", ...theme.scrollbarStyles },
  thread: { maxWidth: 888, margin: "0 auto", padding: "28px 24px 8px", width: "100%", boxSizing: "border-box" },

  row: { display: "flex", marginBottom: 22, alignItems: "flex-start", gap: 12 },
  rowUser: { justifyContent: "flex-end" },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: 8,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.app.dark,
    color: theme.app.lime,
    marginTop: 1,
    "& svg": { fontSize: 17 },
  },
  bubbleUser: {
    maxWidth: "78%",
    background: theme.app.dark,
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 16,
    borderTopRightRadius: 6,
    fontSize: 14.5,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  assist: {
    flex: 1,
    minWidth: 0,
    fontSize: 14.5,
    lineHeight: 1.62,
    color: theme.app.ink,
    "& .md-p": { margin: "0 0 10px" },
    "& .md-p:last-child": { marginBottom: 0 },
    "& .md-ul, & .md-ol": { margin: "4px 0 10px", paddingLeft: 22 },
    "& .md-ul li, & .md-ol li": { margin: "2px 0" },
    "& .md-h": { fontWeight: 600, margin: "8px 0 6px", lineHeight: 1.4 },
    "& .md-h1": { fontSize: 18 },
    "& .md-h2": { fontSize: 16 },
    "& .md-h3": { fontSize: 15 },
    "& .md-code": {
      fontFamily: theme.app.mono,
      fontSize: 12.8,
      background: "#F0F1F3",
      border: `1px solid ${theme.app.border}`,
      borderRadius: 5,
      padding: "1px 5px",
    },
    "& .md-pre": {
      background: "#0F1419",
      color: "#E6E9ED",
      borderRadius: 10,
      padding: "12px 14px",
      overflowX: "auto",
      margin: "4px 0 12px",
      fontFamily: theme.app.mono,
      fontSize: 12.8,
      lineHeight: 1.5,
    },
    "& .md-pre code": { background: "none", border: "none", padding: 0, color: "inherit" },
    "& a": { color: theme.app.info, textDecoration: "none" },
    "& a:hover": { textDecoration: "underline" },
  },

  // pensando (typing)
  dots: { display: "inline-flex", gap: 4, padding: "6px 0" },
  dot: { width: 7, height: 7, borderRadius: "50%", background: theme.app.textTertiary, animation: "$blink 1.3s infinite both" },
  "@keyframes blink": { "0%, 80%, 100%": { opacity: 0.25 }, "40%": { opacity: 1 } },

  // painel de ligação inline (sob a saudação), alinhado ao texto do assistente
  callPanel: { marginLeft: 40, marginTop: -8, marginBottom: 6 },
  callRow: { display: "flex", alignItems: "center", gap: 10 },
  phoneInput: {
    width: 230,
    maxWidth: "60%",
    border: `1px solid ${theme.app.borderInput}`,
    borderRadius: 12,
    padding: "11px 15px",
    fontFamily: FONT,
    fontSize: 15,
    color: theme.app.ink,
    outline: "none",
    transition: "border-color .12s, box-shadow .12s",
    "&::placeholder": { color: theme.app.textTertiary },
    "&:focus": { borderColor: "#B9BEC4", boxShadow: "0 0 0 3px rgba(44,53,61,.06)" },
  },
  callBtn: {
    border: "none",
    borderRadius: 12,
    padding: "11px 20px",
    background: theme.app.dark,
    color: "#fff",
    fontFamily: FONT,
    fontSize: 14.5,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background .12s",
    "&:hover": { background: "#1f262c" },
  },
  callBtnOff: { background: "#E2E4E7", color: "#A6ADB4", cursor: "default", "&:hover": { background: "#E2E4E7" } },
  callChips: { display: "flex", flexWrap: "wrap", gap: 9, marginTop: 12 },
  callChip: {
    border: `1px solid ${theme.app.border}`,
    background: "#fff",
    borderRadius: 999,
    padding: "8px 16px",
    fontSize: 13.5,
    color: theme.app.textSecondary,
    cursor: "pointer",
    transition: "background .12s, border-color .12s, color .12s",
    "&:hover": { background: "#F4F5F7", borderColor: "#D8DBDF", color: theme.app.ink },
  },

  // ---- PORTA (hero): primeira entrada ----
  heroScroll: { flex: 1, overflowY: "auto", display: "flex", ...theme.scrollbarStyles },
  hero: { width: "100%", maxWidth: 640, margin: "auto", padding: "48px 24px", boxSizing: "border-box" },
  heroSpark: {
    width: 42, height: 42, borderRadius: 12, background: theme.app.dark,
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 26,
    "& svg": { fontSize: 21, color: "#CEFF00" },
  },
  heroH1: { fontFamily: FONT, fontWeight: 900, fontSize: 34, letterSpacing: "-0.03em", lineHeight: 1.05, color: theme.app.ink, margin: 0 },
  heroLead: { fontSize: 15.5, color: theme.app.textSecondary, margin: "12px 0 0", lineHeight: 1.5 },
  // campo describe grande (a porta)
  heroField: {
    display: "flex", alignItems: "flex-end", gap: 8, marginTop: 26,
    border: `1px solid ${theme.app.border}`, borderRadius: 14, padding: "10px 10px 10px 16px",
    background: "#fff", boxShadow: "0 1px 2px rgba(16,24,40,.05)",
    transition: "border-color .12s, box-shadow .12s",
    "&:focus-within": { borderColor: theme.app.dark, boxShadow: "0 0 0 3px rgba(44,53,61,.08)" },
  },
  heroTextarea: {
    flex: 1, border: "none", outline: "none", resize: "none", fontFamily: FONT,
    fontSize: 15.5, lineHeight: 1.5, color: theme.app.ink, background: "transparent",
    maxHeight: 260, overflowY: "auto", padding: "7px 0", "&::placeholder": { color: theme.app.textTertiary },
  },
  heroSend: {
    flexShrink: 0, width: 42, height: 42, borderRadius: 11, border: "none",
    background: theme.app.dark, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", transition: "background .12s", "&:hover": { background: "#1f262c" }, "& svg": { fontSize: 22 },
  },
  heroSendOff: { background: "#E2E4E7", color: "#A6ADB4", cursor: "default", "&:hover": { background: "#E2E4E7" } },
  seedsLabel: { fontSize: 12.5, color: theme.app.textTertiary, margin: "26px 0 10px" },
  seedList: { display: "flex", flexDirection: "column", gap: 10 },
  seedCard: {
    display: "flex", alignItems: "center", gap: 14, textAlign: "left", width: "100%",
    border: `1px solid ${theme.app.border}`, borderRadius: 14, padding: "14px 16px", background: "#fff",
    cursor: "pointer", fontFamily: FONT, transition: "border-color .12s, box-shadow .12s, transform .12s",
    "&:hover": { borderColor: "#D8DBDF", boxShadow: "0 4px 16px rgba(27,34,42,.06)" },
    "&:hover $seedArrow": { opacity: 1, transform: "none" },
  },
  seedIcon: { width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 21 } },
  seedText: { flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, color: theme.app.ink, lineHeight: 1.35 },
  seedArrow: { color: theme.app.textTertiary, flexShrink: 0, opacity: 0, transform: "translateX(-4px)", transition: "opacity .12s, transform .12s", "& svg": { fontSize: 20 } },
  heroEscape: {
    display: "block", width: "100%", textAlign: "center", marginTop: 24, padding: 8,
    background: "none", border: "none", cursor: "pointer", fontFamily: FONT,
    fontSize: 13.5, fontWeight: 500, color: theme.app.textSecondary,
    "&:hover": { color: theme.app.ink },
  },

  // composer
  composerWrap: { padding: "10px 24px 20px", background: "#fff" },
  composer: {
    maxWidth: 888,
    margin: "0 auto",
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    border: `1px solid ${theme.app.borderInput}`,
    borderRadius: 18,
    padding: "8px 8px 8px 16px",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    transition: "border-color .12s, box-shadow .12s",
    "&:focus-within": { borderColor: "#B9BEC4", boxShadow: "0 2px 10px rgba(16,24,40,.06)" },
  },
  textarea: {
    flex: 1,
    border: "none",
    outline: "none",
    resize: "none",
    fontFamily: FONT,
    fontSize: 14.5,
    lineHeight: 1.5,
    color: theme.app.ink,
    background: "transparent",
    maxHeight: 200,
    padding: "6px 0",
    "&::placeholder": { color: theme.app.textTertiary },
  },
  sendBtn: {
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "none",
    background: theme.app.dark,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background .12s, opacity .12s",
    "&:hover": { background: "#1f262c" },
    "& svg": { fontSize: 20 },
  },
  sendBtnOff: { background: "#E2E4E7", color: "#A6ADB4", cursor: "default", "&:hover": { background: "#E2E4E7" } },
  hint: { maxWidth: 888, margin: "8px auto 0", textAlign: "center", fontSize: 11.5, color: theme.app.textTertiary },
  buildBtn: {
    marginTop: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    borderRadius: 10,
    padding: "9px 16px",
    background: theme.app.dark,
    color: "#fff",
    fontFamily: FONT,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background .12s",
    "&:hover": { background: "#1f262c" },
    "& svg": { fontSize: 17, color: theme.app.lime },
  },
  callCard: {
    marginTop: 12,
    maxWidth: 460,
    border: `1px solid ${theme.app.border}`,
    borderRadius: 14,
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#fff",
  },
  callCardInfo: { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10 },
  callCardIcon: { fontSize: 22, color: theme.app.textSecondary, flexShrink: 0 },
  callCardTo: { fontSize: 14.5, fontWeight: 600, color: theme.app.ink, fontVariantNumeric: "tabular-nums" },
  callCardTask: { fontSize: 12.5, color: theme.app.textSecondary, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis" },
}));

// chips secundários sob a saudação (label exibido / texto enviado à IA)
const SECONDARY = [
  { label: "Atender ligações com uma IA que agenda meus clientes", send: "Quero atender ligações com uma IA que agenda meus clientes.", build: true },
  { label: "Disparar um WhatsApp quando alguém se cadastrar", send: "Quero disparar um WhatsApp quando alguém se cadastrar.", build: true },
  { label: "Só explorar por enquanto", send: "Por enquanto só quero explorar." },
];

// sementes da porta (hero): ícone na COR FUNCIONAL da categoria + frase de resultado.
// Distantes entre si de propósito — o leque prova a amplitude (handoff §4).
// Catálogo em ./heroSeeds (compartilhado com o hero da nova automação).

const Chat = () => {
  const classes = useStyles();
  const { activeId, createConversation, upsertConversation } = useChat();
  const { user } = useContext(AuthContext);
  const { activeProjectId } = useProject();
  const history = useHistory();
  const greeting = useMemo(() => buildGreeting(user && user.name), [user]);

  // handoff pro Builder: cria um fluxo novo e abre o copiloto já com o spec
  const montarNoBuilder = useCallback(
    spec => {
      const pid = activeProjectId ? `projectId=${activeProjectId}&` : "";
      // vindo da home = DEMO: o copiloto presume padrões e não pede info em tempo real
      history.push(`/applications/new?${pid}demo=1&build=${encodeURIComponent(spec)}`);
    },
    [activeProjectId, history]
  );

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [introReady, setIntroReady] = useState(false); // saudação terminou de "digitar"
  const [call, setCall] = useState(null); // ligação ao vivo: { sessionId, to, status, transcript, answeredAt }
  const [pendingAsk, setPendingAsk] = useState(null); // pergunta estruturada do assistente (ask_user)
  const [launching, setLaunching] = useState(null); // texto sendo entregue ao copiloto (overlay animado)

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);
  const streamingRef = useRef(false);
  const loadedRef = useRef(null); // conversa cujas mensagens já carregamos/possuímos
  const introTimers = useRef([]); // timers da animação da saudação (pensar + digitar)

  const clearIntro = useCallback(() => {
    introTimers.current.forEach(clearTimeout);
    introTimers.current = [];
  }, []);

  // carrega as mensagens quando a conversa ativa muda (seleção no rail).
  // Conversa nova (sem id): a IA "pensa" 3s e digita a saudação, com o mesmo
  // efeito de streaming das demais respostas.
  useEffect(() => {
    clearIntro();
    setCall(null); // troca de conversa encerra o artefato de ligação
    if (activeId == null) {
      if (streamingRef.current) return;
      loadedRef.current = null;
      setIntroReady(false);
      setMessages([{ role: "assistant", content: "" }]); // mostra os pontinhos (pensando)

      const tThink = setTimeout(() => {
        const parts = greeting.split(/(\s+)/); // palavras + espaços, para reconstruir
        let i = 0;
        const type = () => {
          i += 1;
          setMessages([{ role: "assistant", content: parts.slice(0, i).join("") }]);
          if (i < parts.length) {
            introTimers.current.push(setTimeout(type, 45));
          } else {
            setIntroReady(true);
          }
        };
        type();
      }, 3000);
      introTimers.current.push(tThink);
      return clearIntro;
    }

    if (loadedRef.current === activeId) return; // criada localmente / já carregada
    loadedRef.current = activeId;
    setIntroReady(true);
    setMessages([]);
    getMessages(activeId)
      .then(data => setMessages(data.map(m => ({ role: m.role, content: m.content }))))
      .catch(toastError);
    return undefined;
  }, [activeId, greeting, clearIntro]);

  // limpa o streaming/animação pendentes ao desmontar
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current();
      clearIntro();
    };
  }, [clearIntro]);

  // polling do status/transcrição da ligação (a chamada roda toda no backend)
  const sessionId = call && call.sessionId;
  useEffect(() => {
    if (!sessionId) return undefined;
    let stopped = false;
    let timer = null;
    const startedAt = Date.now();
    const loop = async () => {
      if (stopped) return;
      let terminal = false;
      try {
        const d = await getCallStatus(sessionId);
        const st = String(d.status || "").toLowerCase();
        terminal = TERMINAL.includes(st);
        setCall(prev => {
          if (!prev || prev.sessionId !== sessionId) return prev;
          let answeredAt = prev.answeredAt;
          if (isInCall(st) && !answeredAt) answeredAt = { at: Date.now() };
          if (terminal && answeredAt && !answeredAt.endedAt) answeredAt = { ...answeredAt, endedAt: Date.now() };
          return { ...prev, status: d.status, sipStatus: d.sipStatus, sipReason: d.sipReason, result: d.result || null, transcript: d.transcript || [], answeredAt };
        });
      } catch (e) {
        /* erro transitório: tenta de novo */
      }
      if (stopped) return;
      if (terminal || Date.now() - startedAt > 5 * 60 * 1000) return; // encerrou (ou timeout de segurança)
      timer = setTimeout(loop, 1500);
    };
    loop();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  // auto-scroll ao final
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // auto-grow do textarea
  const autoGrow = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    // cresce até ~10 linhas (pedido longo); acima disso, rola
    ta.style.height = `${Math.min(ta.scrollHeight, 260)}px`;
  }, []);
  useEffect(autoGrow, [input, autoGrow]);

  const sendText = useCallback(async raw => {
    const content = (raw || "").trim();
    if (!content || streamingRef.current) return;

    setPendingAsk(null); // qualquer envio fecha uma pergunta aberta
    clearIntro(); // interrompe a animação da saudação, se estiver rolando

    let convId = activeId;
    let base = null;
    if (!convId) {
      try {
        // semeia a saudação no servidor para a IA ter contexto da conversa
        const conv = await createConversation(greeting);
        convId = conv.id;
        loadedRef.current = conv.id; // evita o effect recarregar (sobrescrever) as mensagens
        base = [{ role: "assistant", content: greeting }]; // saudação completa (caso a animação não tenha terminado)
      } catch (err) {
        toastError(err);
        return;
      }
    }

    setMessages(prev => [...(base || prev), { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    streamingRef.current = true;

    abortRef.current = streamMessage(convId, content, {
      onDelta: delta => {
        setMessages(prev => {
          const next = prev.slice();
          const last = next.length - 1;
          next[last] = { ...next[last], content: next[last].content + delta };
          return next;
        });
      },
      onAsk: spec => {
        // pergunta estruturada: a bolha vazia do assistente sai (a pergunta vai
        // no widget); a resposta volta como a próxima mensagem do usuário
        setMessages(prev => {
          const next = prev.slice();
          const last = next.length - 1;
          if (next[last] && next[last].role === "assistant" && !next[last].content) next.pop();
          return next;
        });
        setPendingAsk(spec);
      },
      onDone: info => {
        setStreaming(false);
        streamingRef.current = false;
        abortRef.current = null;
        if (info && info.title) {
          upsertConversation({ id: convId, title: info.title, updatedAt: new Date().toISOString() });
        }
      },
      onError: err => {
        setStreaming(false);
        streamingRef.current = false;
        abortRef.current = null;
        setMessages(prev => {
          const next = prev.slice();
          const last = next.length - 1;
          if (next[last] && next[last].role === "assistant" && !next[last].content) {
            next[last] = { ...next[last], content: `⚠️ ${err.message || "Falha ao responder."}` };
          }
          return next;
        });
      },
    });
  }, [activeId, createConversation, upsertConversation, greeting, clearIntro]);

  // CRIA o fluxo no servidor já no lançamento (assim aparece em "Conversas
  // recentes" na hora e o appId fica estável) e devolve a URL do Builder semeado.
  const createFlowUrl = useCallback(
    async text => {
      const q = activeProjectId ? `projectId=${activeProjectId}&` : "";
      try {
        const { data } = await api.post("/applications", {
          name: text.slice(0, 60),
          projectId: activeProjectId || undefined,
        });
        const id = data.code || data.id;
        return `/applications/${id}?${q}demo=1&build=${encodeURIComponent(text)}`;
      } catch (e) {
        // fallback: segue como fluxo novo não-persistido
        return `/applications/new?${q}demo=1&build=${encodeURIComponent(text)}`;
      }
    },
    [activeProjectId]
  );

  // entrega a descrição ao COPILOTO do canvas (mesmo cérebro): mostra o overlay
  // "minimizando pro canto" e, em paralelo, cria o fluxo; navega quando a animação
  // passou (~700ms) E o fluxo já existe — o copiloto brota do canto e continua.
  const launchCopilot = useCallback(
    content => {
      const text = (content || "").trim();
      if (!text) return;
      clearIntro();
      setLaunching(text);
      const nav = createFlowUrl(text);
      const wait = new Promise(r => window.setTimeout(r, 700));
      Promise.all([nav, wait]).then(([url]) => history.push(url));
    },
    [clearIntro, createFlowUrl, history]
  );

  const handleSend = useCallback(() => {
    const content = input.trim();
    if (!content) return;
    setInput("");
    launchCopilot(content);
  }, [input, launchCopilot]);

  // resposta do widget de pergunta → vira a próxima mensagem do usuário
  const submitAnswer = useCallback(
    value => {
      const text = Array.isArray(value) ? value.join(", ") : String(value);
      setPendingAsk(null);
      sendText(text);
    },
    [sendText]
  );

  // para a geração em andamento (aborta o stream e limpa o estado de "pensando")
  const handleStop = useCallback(() => {
    if (abortRef.current) abortRef.current();
    abortRef.current = null;
    setStreaming(false);
    streamingRef.current = false;
  }, []);

  const onKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // dispara a ligação real (a IA liga e conversa). task opcional = objetivo (a IA
  // liga em nome do usuário); sem task = demonstração. Polling acompanha o status.
  const startCall = useCallback(
    async (to, task) => {
      const num = String(to || "").trim();
      if (!num || call) return;
      try {
        // amarra a ligação à conversa atual → o resultado volta aqui no chat
        const r = await placeCall(num, task, task ? activeId : undefined);
        setCall({ sessionId: r.sessionId, to: r.to || num, task: task || null, status: r.status || "queued", transcript: [], answeredAt: null });
      } catch (err) {
        toastError(err);
      }
    },
    [call, activeId]
  );


  const canSend = input.trim() && !streaming;
  // PORTA (hero): home fresca, sem conversa aberta e sem ligação — o primeiro
  // quadro é a porta "descreva → copiloto", não o thread de chat.
  const showHero = activeId == null && !call;
  // painel de ligação: aparece quando a IA termina de saudar (só a saudação na
  // tela) e ainda não há uma ligação em andamento
  const showCallPanel =
    !call &&
    introReady &&
    !streaming &&
    messages.length === 1 &&
    messages[0].role === "assistant" &&
    !!messages[0].content;

  const composer = (
    <div className={classes.composer}>
      <textarea
        ref={textareaRef}
        className={classes.textarea}
        placeholder="Ex.: atender ligações com uma IA que agenda meus clientes"
        rows={1}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={onKeyDown}
      />
      {streaming ? (
        <button type="button" className={classes.sendBtn} onClick={handleStop} aria-label="Parar">
          <StopIcon />
        </button>
      ) : (
        <button
          type="button"
          className={`${classes.sendBtn} ${canSend ? "" : classes.sendBtnOff}`}
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Enviar"
        >
          <SendIcon />
        </button>
      )}
    </div>
  );

  // overlay de "minimizar pro canto" (mostra a frase indo pro copiloto no canvas)
  const launchNode = launching ? (
    <div className={classes.launchOverlay}>
      <div className={classes.launchCard}>
        <div className={classes.launchHead}>
          <span className={classes.launchSparkle}><Sparkle /></span>
          <span className={classes.launchTitle}>
            Montando seu fluxo
            <span className={classes.dots} style={{ marginLeft: 4 }}>
              <span className={classes.dot} /><span className={classes.dot} style={{ animationDelay: ".15s" }} /><span className={classes.dot} style={{ animationDelay: ".3s" }} />
            </span>
          </span>
        </div>
        <div className={classes.launchBubble}>{launching}</div>
      </div>
    </div>
  ) : null;

  if (showHero) {
    return (
      <div className={classes.root}>
        <div className={classes.heroScroll}>
          <div className={classes.hero}>
            <div className={classes.heroSpark}><Sparkle /></div>
            <h1 className={classes.heroH1}>O que você quer montar?</h1>
            <p className={classes.heroLead}>Descreva em uma frase. A Numera monta na sua frente — sem configurar nada agora.</p>
            <div className={classes.heroField}>
              <textarea
                ref={textareaRef}
                className={classes.heroTextarea}
                placeholder="Ex.: atender ligações com uma IA que agenda meus clientes"
                rows={1}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                autoFocus
              />
              <button
                type="button"
                className={`${classes.heroSend} ${canSend ? "" : classes.heroSendOff}`}
                onClick={handleSend}
                disabled={!canSend}
                aria-label="Enviar"
              >
                <SendIcon />
              </button>
            </div>
            <div className={classes.seedsLabel}>Ou comece por um destes:</div>
            <div className={classes.seedList}>
              {SEEDS.map(s => (
                <button key={s.label} type="button" className={classes.seedCard} onClick={() => launchCopilot(s.send)}>
                  <span className={classes.seedIcon} style={{ background: hexA(s.color, 0.12), color: s.color }}>
                    <s.icon />
                  </span>
                  <span className={classes.seedText}>{s.label}</span>
                  <span className={classes.seedArrow}><ArrowFwdIcon /></span>
                </button>
              ))}
            </div>
            <button type="button" className={classes.heroEscape} onClick={() => history.push("/applications")}>
              Só explorar por enquanto →
            </button>
          </div>
        </div>
        <div className={classes.hint} style={{ padding: "0 24px 16px" }}>O assistente pode cometer erros. Confira informações importantes.</div>
        {launchNode}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.scroll} ref={scrollRef}>
        <div className={classes.thread}>
          {messages.map((m, idx) =>
            m.role === "user" ? (
              <div key={idx} className={`${classes.row} ${classes.rowUser}`}>
                <div className={classes.bubbleUser}>{m.content}</div>
              </div>
            ) : (
              <div key={idx} className={classes.row}>
                <div className={classes.avatar}>
                  <Sparkle />
                </div>
                <div className={classes.assist}>
                  {m.content ? (
                    (() => {
                      const mk = parseMarkers(m.content);
                      return (
                        <>
                          {mk.clean && <Markdown text={mk.clean} />}
                          {mk.build && !streaming && (
                            <button type="button" className={classes.buildBtn} onClick={() => montarNoBuilder(mk.build)}>
                              <BuildIcon /> Montar no Builder
                            </button>
                          )}
                          {mk.call && !streaming && !call && (
                            <div className={classes.callCard}>
                              <div className={classes.callCardInfo}>
                                <PhoneIcon className={classes.callCardIcon} />
                                <div>
                                  <div className={classes.callCardTo}>{mk.call.to}</div>
                                  {mk.call.task && <div className={classes.callCardTask}>{mk.call.task}</div>}
                                </div>
                              </div>
                              <button
                                type="button"
                                className={classes.buildBtn}
                                style={{ marginTop: 0, flexShrink: 0 }}
                                onClick={() => startCall(mk.call.to, mk.call.task)}
                              >
                                <PhoneIcon style={{ fontSize: 16 }} /> Ligar agora
                              </button>
                            </div>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    <div className={classes.dots}>
                      <span className={classes.dot} style={{ animationDelay: "0s" }} />
                      <span className={classes.dot} style={{ animationDelay: ".2s" }} />
                      <span className={classes.dot} style={{ animationDelay: ".4s" }} />
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          {/* painel de ligação inline (aparece sob a saudação) */}
          {showCallPanel && (
            <div className={classes.callPanel}>
              <div className={classes.callChips}>
                {SECONDARY.map(s => (
                  <div key={s.label} className={classes.callChip} onClick={() => (s.build ? launchCopilot(s.send) : sendText(s.send))}>
                    {s.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* artefato de chamada ao vivo (orbe + status + transcrição) */}
          {call && (
            <CallArtifact
              to={call.to}
              status={call.status}
              sipStatus={call.sipStatus}
              sipReason={call.sipReason}
              result={call.result}
              transcript={call.transcript}
              answeredAt={call.answeredAt}
            />
          )}

          {/* pergunta estruturada do assistente (ask_user) */}
          {pendingAsk && !streaming && (
            <div className={classes.row}>
              <div className={classes.avatar}>
                <Sparkle />
              </div>
              <div className={classes.assist} style={{ maxWidth: 560, width: "100%" }}>
                <AskWidget spec={pendingAsk} onSubmit={submitAnswer} />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={classes.composerWrap}>
        {composer}
        <div className={classes.hint}>O assistente pode cometer erros. Confira informações importantes.</div>
      </div>

      {launchNode}
    </div>
  );
};

export default Chat;
